/**
 * Orbital Tank — Indirect fire specialist. Death from above.
 * Marker determines WHERE, beam comes from above. Bypasses cover.
 */

import type { DeferredSpawn } from '../types';
import { registerWeapon, lookupWeapon, createProjectile } from '../weapons';
import { clearVerticalBeam, applyScorch } from '../terrain/grid';
import { registerTank } from './registry';
import { DEFAULT_CRATER, HUGE_CRATER, SMALL_CRATER } from '../terrain/crater-shapes';

// ─── Sub-weapons ─────────────────────────────────────────────────────────────

registerWeapon({
  id: 'orbital_beam',
  name: 'Orbital Beam',
  description: '',
  tier: 'SUB',
  rarity: 'common',
  projectileRadius: 6,
  projectileSpeed: 0,
  color: 0xff8800,
  gravity: 0,
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: 30,
  blastRadius: 90,
  knockbackMult: 0.08,
  knockbackRadius: 1.3,
  shakeIntensity: 2.5,
  particleScale: 1.5,
  craterShape: DEFAULT_CRATER,
  knockbackAngleBias: { angle: Math.PI / 2, strength: 0.5 },
  onTick(proj, _state, fx) {
    // Immediate detonation — beam lands instantly
    proj.ignoreTerrain = true;
    proj.ignoreTanks = true;
    proj.ignoreWind = true;
    const beamBounds = clearVerticalBeam(proj.x, proj.y, 25);
    if (beamBounds) {
      applyScorch(proj.x, proj.y, 60);
      fx.push({ type: 'terrain_update', bounds: beamBounds });
    }
    // Beam VFX — particles along the column
    for (let py = 0; py < proj.y; py += 80) {
      fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [proj.x, py, 0xff8800, 15, 0.2] });
    }
    return 'detonate';
  },
});

// Judgement sub-weapon — delayed heavy beam with buildup
registerWeapon({
  id: 'orbital_judgement',
  name: 'Judgement Beam',
  description: '',
  tier: 'SUB',
  rarity: 'common',
  projectileRadius: 8,
  projectileSpeed: 0,
  color: 0xff6600,
  gravity: 0,
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: 55,
  blastRadius: 160,
  knockbackMult: 0.24,
  knockbackRadius: 1.5,
  shakeIntensity: 1.5,
  particleScale: 3.0,
  craterShape: HUGE_CRATER,
  canDestroyWireframe: true,
  wireframeDamageScale: 0.6,
  knockbackAngleBias: { angle: Math.PI / 2, strength: 0.85 },
  onTick(proj, _state, fx) {
    proj.ignoreTerrain = true;
    proj.ignoreTanks = true;
    proj.ignoreWind = true;
    proj.behaviorState.lifetime = (proj.behaviorState.lifetime || 0) + 1;
    const t = proj.behaviorState.lifetime;

    // ── Phase 1: Illumination (frames 1-150) — thin beam marks target ──
    if (t === 1) {
      // Light pierces the sky — thin golden beam from sky to target
      fx.push({ type: 'particles', method: 'spawnBeamLine', args: [proj.x, 0, proj.y, 0xffaa22, 2, 2.5] });
      fx.push({ type: 'screen_flash', color: 0xffaa22, alpha: 0.08 });
    }
    if (t <= 150) {
      // Gentle golden glow at target every ~30 frames
      if (t % 30 === 0) {
        fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0xffaa22, 3] });
      }
      // Subtle building shake every 30 frames, intensity 0.5→2
      if (t % 30 === 0) {
        const progress = t / 150;
        fx.push({ type: 'screen_shake', intensity: 0.5 + progress * 1.5, decay: 0.85 });
      }

      // ── Phase 2: Golden bolt descends (frames 125-150) ──
      if (t === 125) {
        // Bolt init — thick beam intensifies on top of the thin one
        proj.behaviorState.boltY = 0;
        proj.behaviorState.boltSpeed = proj.y / 25;
        fx.push({ type: 'particles', method: 'spawnBeamLine', args: [proj.x, 0, proj.y, 0xffcc00, 8, 0.6] });
        fx.push({ type: 'screen_flash', color: 0xffcc00, alpha: 0.12 });
      }
      if (t >= 125 && t <= 150) {
        // Bolt descends
        proj.behaviorState.boltY = (proj.behaviorState.boltY || 0) + (proj.behaviorState.boltSpeed || proj.y / 25);
        const boltY = proj.behaviorState.boltY;
        // Gold particles at bolt head
        for (let i = 0; i < 5; i++) {
          fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, boltY, 0xffcc00, 2] });
        }
        // White-hot core particles
        for (let i = 0; i < 3; i++) {
          fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, boltY, 0xffffff, 2] });
        }
        // Trailing shockwave rings every 5 frames
        if (t % 5 === 0) {
          fx.push({ type: 'particles', method: 'spawnShockwave', args: [proj.x, boltY, 0xffcc00, 40, 200, 1.5, 0.3] });
        }
        // Growing screen shake during descent
        const descentProgress = (t - 125) / 25;
        fx.push({ type: 'screen_shake', intensity: 3 + descentProgress * 10, decay: 0.88 });
      }

      return null;
    }

    // ── Phase 3: Detonation (frame 151) — finger of god ──
    const beamBounds = clearVerticalBeam(proj.x, proj.y, 60);
    if (beamBounds) {
      applyScorch(proj.x, proj.y, 120);
      fx.push({ type: 'terrain_update', bounds: beamBounds });
    }
    // Massive localized explosion at impact
    fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [proj.x, proj.y, 0xffcc00, 160, 3.0] });
    fx.push({ type: 'particles', method: 'spawnShockwave', args: [proj.x, proj.y, 0xffcc00, 250, 700, 3.0, 0.35] });
    // Smoke puff — 8 dark particles
    fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0x332200, 8] });
    fx.push({ type: 'screen_shake', intensity: 25, decay: 0.88 });
    fx.push({ type: 'screen_flash', color: 0xffffff, alpha: 0.35 });
    fx.push({ type: 'announce_world', text: 'JUDGMENT!', x: proj.x, y: proj.y - 60, color: 0xffcc00, fontSize: 52, hold: 1.2 });
    return 'detonate';
  },
});

// Beacon strike sub-weapon — countdown → blue beam → detonation
registerWeapon({
  id: 'orbital_beacon_strike',
  name: 'Beacon Strike',
  description: '',
  tier: 'SUB',
  rarity: 'common',
  projectileRadius: 6,
  projectileSpeed: 0,
  color: 0x4488ff,
  gravity: 0,
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: 23,           // S1 nerf: was 35
  blastRadius: 100,
  knockbackMult: 0.10,
  knockbackRadius: 1.2,
  shakeIntensity: 2.5,
  particleScale: 1.5,
  craterShape: DEFAULT_CRATER,
  knockbackAngleBias: { angle: -Math.PI / 2, strength: 0.5 },
  onTick(proj, _state, fx) {
    proj.ignoreTerrain = true;
    proj.ignoreTanks = true;
    proj.ignoreWind = true;
    proj.behaviorState.lifetime = (proj.behaviorState.lifetime || 0) + 1;
    const t = proj.behaviorState.lifetime;

    // ── Phase 1: Charge buildup (frames 1-90) — blue beam line + escalating shake ──
    if (t === 1) {
      // Blue beam line appears from sky to target
      fx.push({ type: 'particles', method: 'spawnBeamLine', args: [proj.x, 0, proj.y, 0x4488ff, 3, 2.5] });
      fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0x4488ff, 5] });
      fx.push({ type: 'screen_flash', color: 0x4488ff, alpha: 0.1 });
    }

    if (t <= 90) {
      // Charge particles at target (every 10 frames)
      if (t % 10 === 0) {
        fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0x4488ff, 3] });
      }
      // Escalating screen shake (every 12 frames)
      if (t % 12 === 0) {
        const progress = t / 90;
        fx.push({ type: 'screen_shake', intensity: 1 + progress * 5, decay: 0.85 });
      }

      // ── Energy bolt descends (frames 65-90) ──
      if (t === 65) {
        proj.behaviorState.boltY = 0;
        proj.behaviorState.boltSpeed = proj.y / 25;
        fx.push({ type: 'particles', method: 'spawnBeamLine', args: [proj.x, 0, proj.y, 0x66bbff, 6, 0.8] });
        fx.push({ type: 'screen_flash', color: 0x4488ff, alpha: 0.12 });
      }
      if (t >= 65 && t <= 90) {
        const boltY = proj.behaviorState.boltY = (proj.behaviorState.boltY || 0) + (proj.behaviorState.boltSpeed || proj.y / 25);
        fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, boltY, 0x66bbff, 3] });
        if (t % 2 === 0) {
          fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, boltY, 0xffffff, 2] });
        }
        const descentProgress = (t - 65) / 25;
        fx.push({ type: 'screen_shake', intensity: 3 + descentProgress * 8, decay: 0.88 });
      }

      return null;
    }

    // ── Phase 2: Detonation (frame 91) — damage + knockback applied here ──
    const beamBounds = clearVerticalBeam(proj.x, proj.y, 25);
    if (beamBounds) {
      applyScorch(proj.x, proj.y, 60);
      fx.push({ type: 'terrain_update', bounds: beamBounds });
    }
    fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [proj.x, proj.y, 0xffaa22, 100, 1.5] });
    fx.push({ type: 'particles', method: 'spawnShockwave', args: [proj.x, proj.y, 0xffaa22, 150, 500, 2.5, 0.35] });
    fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0x443322, 6] });
    fx.push({ type: 'screen_shake', intensity: 12, decay: 0.88 });
    fx.push({ type: 'screen_flash', color: 0xffaa22, alpha: 0.2 });
    fx.push({ type: 'announce_world', text: 'STRIKE!', x: proj.x, y: proj.y - 40, color: 0xffaa22, fontSize: 36, hold: 0.6 });
    return 'detonate';
  },
});

// ─── Shot 1: Beacon — marker lands, countdown, beam strike from above ────────

const ORBITAL_SHOT1 = registerWeapon({
  id: 'orbital_shot1',
  name: 'Beacon',
  description: 'Marks a target. 3-2-1 countdown, then a beam strike from above.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 7,
  projectileSpeed: 0.55,
  color: 0xaabbcc,
  gravity: 0.15,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 0,
  blastRadius: 20,
  knockbackMult: 0,
  knockbackRadius: 0.5,
  shakeIntensity: 5.0,
  particleScale: 0.3,
  craterShape: SMALL_CRATER,
  edgeguard: { chargeFrames: 35, threshold: 40 },
  // Blinking signal pulses — cold blue-white strobe, 2 on / 4 off cycle
  onTick(proj, _state, fx) {
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    const phase = proj.behaviorState.t % 6;
    // Strobe: emit on frames 0-1, dark on 2-5
    if (phase < 2) {
      const color = Math.random() < 0.6 ? 0xccddff : 0xffffff;
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x, proj.y, color,
        0.2, 0.8, 0.04, 0.08, 0.15, 0.3, 0, 0.92, true, true, 0.6,
      ] });
    }
    return null;
  },
  onImpact(proj, x, y, _hitType, _hitIndex, state, fx) {
    const strikeWeapon = lookupWeapon('orbital_beacon_strike');
    const strike = createProjectile(
      strikeWeapon, x, y, 0, 0,
      proj.owner, state.frameCount, 1,
    );
    strike.ignoreTerrain = true;
    strike.ignoreTanks = true;
    strike.ignoreWind = true;
    strike.behaviorState.lifetime = 0;
    fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [x, y, 0x4488ff, 20, 0.15] });
    fx.push({ type: 'screen_shake', intensity: 3, decay: 0.9 });
    return [{
      spawnFrame: state.frameCount + 2,
      spawned: false,
      projectile: strike,
    }];
  },
});

// ─── Shot 2: Judgement — weak marker, spawns delayed heavy beam sub ──────────

const ORBITAL_SHOT2 = registerWeapon({
  id: 'orbital_shot2',
  name: 'Judgement',
  description: 'Weak marker. A beam of light marks the target, then a golden bolt descends from the sky.',
  tier: 'UNCOMMON',
  rarity: 'uncommon',
  projectileRadius: 7,
  projectileSpeed: 0.50,
  color: 0xffeedd,
  pulseEffect: true,
  gravity: 0.15,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 10,
  blastRadius: 40,
  knockbackMult: 0.03,
  knockbackRadius: 0.8,
  shakeIntensity: 4.0,
  particleScale: 0.5,
  craterShape: SMALL_CRATER,
  edgeguard: { chargeFrames: 35, threshold: 40 },
  // Golden divine wisps + white-hot sparks — ascending energy feel
  onTick(proj, _state, fx) {
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    // Golden wisps every 2 frames — float upward (negative gravity)
    if (proj.behaviorState.t % 2 === 0) {
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x + (Math.random() - 0.5) * 8,
        proj.y + (Math.random() - 0.5) * 8,
        0xffd700,
        0.2, 0.6, 0.08, 0.15, 0.2, 0.45, -0.02, 0.95, true, false, 0.5,
      ] });
    }
    // White-hot sparks every 4 frames — crisp, fast
    if (proj.behaviorState.t % 4 === 0) {
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x, proj.y, 0xffffff,
        0.4, 1.2, 0.03, 0.06, 0.1, 0.2, -0.01, 0.9, true, true, 0.7,
      ] });
    }
    return null;
  },
  onImpact(proj, x, y, _hitType, _hitIndex, state, fx) {
    // Spawn Judgement beam sub at impact point
    const judgementWeapon = lookupWeapon('orbital_judgement');
    const beam = createProjectile(
      judgementWeapon, x, y, 0, 0,
      proj.owner, state.frameCount, 1,
    );
    beam.ignoreTerrain = true;
    beam.ignoreTanks = true;
    beam.ignoreWind = true;
    beam.behaviorState.lifetime = 0;
    fx.push({ type: 'announce_world', text: 'INCOMING!', x, y: y - 40, color: 0xff6600, fontSize: 32, hold: 0.6 });
    return [{
      spawnFrame: state.frameCount + 2,
      spawned: false,
      projectile: beam,
    }];
  },
});

registerTank({
  id: 'orbital',
  name: 'Orbital',
  description: 'Indirect fire specialist. Death from above.',
  visual: { bodyColor: 0x7788AA, glowColor: 0x8888FF, bodyRadius: 21 },
  ecbRadius: 18,
  landingMultiplier: 1.2,
  shots: [
    { tier: 1, weapon: ORBITAL_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Beacon', tierColor: 0xaabbcc },
    { tier: 2, weapon: ORBITAL_SHOT2, momentumCost: 0, hitlagBonus: 33, label: 'Judgement', tierColor: 0xffeedd },
  ],
});
